<template>
  <div
    :class="{
      'product-detail-head-fi': true,
      'product-detail-head-fi--no-margin-bottom': noMarginBottom,
    }"
  >
    <template v-if="catalogData">
      <grid-container-fi>
        <div class="fi-col-start-1 fi-col-end-13 md:fi-col-end-7">
          <catalog-slider-fi
            :catalog-data="catalogData"
            :item-name="productName"
          />
        </div>
        <div class="fi-col-start-1 fi-col-end-13 md:fi-col-start-7 md:fi-col-end-13">
          <!-- eslint-disable vue/no-v-html -->
          <headline-fi
            v-if="articleNumber"
            class="product-detail-head-fi__art-nr"
            tag="div"
            color="brand"
            :size="24"
          >
            <span
              data-test="article-number"
              v-html="articleNumber"
            />
          </headline-fi>

          <div
            v-if="eanCode"
            class="product-detail-head-fi__ean-code"
          >
            {{ eanCode }}
          </div>

          <headline-fi
            v-if="productName"
            class="product-detail-head-fi__name"
            tag="h1"
            :size="48"
          >
            <span v-html="productName" />
          </headline-fi>
          <!-- eslint-enable vue/no-v-html -->

          <availability-status-view-only-fi
            v-if="hasAvailabilityStatusOnlyPermission && isLG"
            :fields="fields"
          />

          <div
            v-else-if="isShop && !hasAvailabilityStatusOnlyPermission"
            id="buy-box-desktop"
            class="product-detail-head-fi__buy-box"
          />

          <div class="product-detail-head-fi__links">
            <link-fi
              v-if="categorySlug"
              class="product-detail-head-fi__link"
              :href="categorySlug"
              :size="18"
              data-test="product-variants-link"
            >
              {{ $dict.get('product-Family.Anchor-Link-Text') }}
            </link-fi>

            <cad-link-fi
              v-if="hasCADLink"
              class="product-detail-head-fi__link"
              :fields="fields"
              :cad-url="cadUrl"
              open-in-dialog
              @open-cad-details-dialog="onOpenCadDetailsDialog"
            />

            <link-fi
              v-if="
                fields.customLink?.value &&
                (fields.customLink.value.href || fields.customLink.value.url)
              "
              class="product-detail-head-fi__link"
              :href="
                fields.customLink.value.href
                  ? fields.customLink.value.href
                  : fields.customLink.value.url
              "
              :external="Boolean(fields.customLink.value.url)"
              :target="fields.customLink.value.target"
              :size="18"
            >
              {{ fields.customLink.value.text }}
            </link-fi>

            <link-fi
              v-if="mediaDownloadLink && fields.mediaDownloadLabel?.value"
              class="product-detail-head-fi__link"
              href="#"
              rel="nofollow"
              :size="18"
              @click.stop.prevent="openMediaDownloadDialog"
            >
              {{ fields.mediaDownloadLabel.value }}
            </link-fi>
          </div>
          <grid-columns-fi :columns="6">
            <div class="fi-col-span-6 lg:fi-col-span-5">
              <div class="product-detail-head-fi__features">
                <text-expander-fi
                  v-if="features"
                  :content="{ value: features }"
                  :max-height="400"
                  :expand-text="$dict.get('global-Labels.Expander.Expand')"
                  :reduce-text="$dict.get('global-Labels.Expander.Reduce')"
                />
              </div>

              <div
                v-if="
                  !hasAvailabilityStatusOnlyPermission && (isRetailConnect || isRetailConnectCart)
                "
                class="product-detail-head-fi__retail-connect-cart-actions product-detail-head-fi__retail-connect-cart-actions--md"
              >
                <button-fi
                  v-if="localRetailers"
                  :disabled="!hasLocalRetailers"
                  type="button"
                  class="button-fi__primary--large"
                  data-test="retail-connect-cart-action-button"
                  data-test-2="retail-connect-local-retailers"
                  @click.stop.prevent="openRetailSearchDialog"
                >
                  {{ $dict.get('dealer-Locator.Find-your-local-Dealer') }}
                </button-fi>

                <button-fi
                  v-if="isRetailConnect"
                  type="button"
                  data-test="retail-connect-cart-action-button"
                  data-test-2="retail-connect-retail-connect"
                  :class="[
                    'button__find-retail-connect',
                    localRetailers ? 'button-fi__tertiary--large' : 'button-fi__primary--large',
                  ]"
                  :disabled="retailers !== null && retailers.length === 0"
                  @click.stop.prevent="openProductRetailerDialog"
                >
                  {{ $dict.get('online-Shop.Product.Retail-Connect.Buy-Product') }}
                </button-fi>

                <button-fi
                  v-if="isRetailConnectCart"
                  class="button-fi__tertiary--large"
                  type="button"
                  data-test="retail-connect-cart-action-button"
                  data-test-2="retail-connect-retail-connect-cart"
                  :disabled="Boolean(retailerNotAvailable || productInCart || addToCartLoading)"
                  @click.stop.prevent="addProduct"
                >
                  {{
                    productInCart
                      ? $dict.get('online-Shop.Product.Already-in-Cart')
                      : $dict.get('online-Shop.Product.Add-to-Cart')
                  }}
                </button-fi>
              </div>
            </div>
          </grid-columns-fi>
        </div>

        <media-download-dialog-fi
          ref="mediaDownloadDialog"
          :media-download-link="mediaDownloadLink"
          :file-name="mediaDownloadFileName"
          :fields="fields"
        />

        <retail-search-dialog-fi
          ref="retailSearchDialog"
          :fields="fields"
          :catalog-id="catalogData.ProductId"
          :product-name="productName"
        />

        <product-retailer-dialog-fi
          ref="productRetailerDialog"
          :retailers="retailers"
          :retailer-facets="retailerFacets"
        />
      </grid-container-fi>

      <availability-status-view-only-fi
        v-if="hasAvailabilityStatusOnlyPermission && !isLG"
        :fields="fields"
      />

      <span
        v-intersect="onIntersect"
        class="sticky-actions-intersector"
      />
      <div
        v-if="!hasAvailabilityStatusOnlyPermission"
        :class="{
          'product-detail-head-fi__actions-mobile': true,
          'product-detail-head-fi__actions-mobile--sticky': stickyEnabled,
        }"
      >
        <div
          v-if="isShop"
          class="product-detail-head-fi__buy-box"
        >
          <teleport
            v-if="productDetailHeadMounted"
            to="#buy-box-desktop"
            :disabled="!isMD"
          >
            <buy-box-module @added-to-memolist="$emit('added-to-memolist')" />
          </teleport>
        </div>

        <div
          v-else-if="isRetailConnect || isRetailConnectCart || localRetailers"
          class="product-detail-head-fi__retail-connect-cart-actions"
        >
          <button-fi
            v-if="localRetailers"
            type="button"
            class="button-fi__primary--large button__find-retailer"
            data-test="retail-connect-cart-action-button"
            data-test-2="retail-connect-local-retailers"
            :disabled="!hasLocalRetailers"
            @click.stop.prevent="openRetailSearchDialog"
          >
            {{ $dict.get('dealer-Locator.Find-your-local-Dealer') }}
          </button-fi>

          <button-fi
            v-if="isRetailConnect"
            type="button"
            data-test="retail-connect-cart-action-button"
            data-test-2="retail-connect-retail-connect"
            :class="[
              'button__find-retail-connect',
              localRetailers ? 'button-fi__tertiary--large' : 'button-fi__primary--large',
            ]"
            :disabled="retailers !== null && retailers.length === 0"
            @click.stop.prevent="openProductRetailerDialog"
          >
            {{ $dict.get('online-Shop.Product.Retail-Connect.Buy-Product') }}
          </button-fi>

          <button-fi
            v-if="isRetailConnectCart"
            type="button"
            class="button-fi__tertiary--large button__add-to-cart"
            data-test="retail-connect-cart-action-button"
            data-test-2="retail-connect-retail-connect-cart"
            :disabled="Boolean(retailerNotAvailable || productInCart || addToCartLoading)"
            @click.stop.prevent="addProduct"
          >
            {{
              productInCart
                ? $dict.get('online-Shop.Product.Already-in-Cart')
                : $dict.get('online-Shop.Product.Add-to-Cart')
            }}
          </button-fi>
        </div>
      </div>

      <cad-details-dialog-fi
        ref="cadDetailsDialog"
        :fields="fields"
        :cad-url="cadUrl"
        lazy
      />
    </template>

    <div id="price-row-savings" />
  </div>
</template>

<script>
import { ButtonFi, HeadlineFi, LinkFi } from 'atoms';
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';

import CadLinkFi from '@/components/cad-link-fi/CadLinkFi.vue';
import CatalogSliderFi from '@/components/catalog-slider-fi/CatalogSliderFi.vue';
import GridColumnsFi from '@/components/grid-fi/GridColumnsFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import CadDetailsDialogFi from '@/components/product-detail/cad-details-dialog-fi/CadDetailsDialogFi.vue';
import MediaDownloadDialogFi from '@/components/product-detail/media-download-dialog-fi/MediaDownloadDialogFi.vue';
import ProductRetailerDialogFi from '@/components/product-detail/product-retailer-dialog-fi/ProductRetailerDialogFi.vue';
import RetailSearchDialogFi from '@/components/product-detail/retail-search-dialog-fi/RetailSearchDialogFi.vue';
import BuyBoxModule from '@/components/shop-modules/buy-box-module/BuyBoxModule.vue';
import TextExpanderFi from '@/components/text-expander-fi/TextExpanderFi.vue';
import GET_RETAILER_AVAILABILITY_FOR_IDS from '@/graphql/getRetailerAvailabilityForIds.gql';
import HAS_LOCAL_RETAILERS from '@/graphql/hasLocalRetailers.gql';
import accessPrivilegesMixin from '@/mixins/accessPrivilegesMixin.vue';
import breakpointMixin from '@/mixins/breakpointMixin.vue';
import catalogAttributesMixin from '@/mixins/catalogAttributesMixin.vue';
import catalogTextListMixin from '@/mixins/catalogTextListMixin.vue';
import onlineRetailersMixin from '@/mixins/onlineRetailersMixin.vue';
import shoppingcartMixin from '@/mixins/shoppingcartMixin.vue';

const AvailabilityStatusViewOnlyFi = defineAsyncComponent(
  () =>
    import(
      '@/components/product-detail/product-detail-head-fi/availability-status-view-only-fi/AvailabilityStatusViewOnlyFi.vue'
    ),
);

export default {
  name: 'ProductDetailHeadFi',
  components: {
    ButtonFi,
    CatalogSliderFi,
    GridContainerFi,
    GridColumnsFi,
    HeadlineFi,
    LinkFi,
    MediaDownloadDialogFi,
    RetailSearchDialogFi,
    AvailabilityStatusViewOnlyFi,
    ProductRetailerDialogFi,
    TextExpanderFi,
    BuyBoxModule,
    CadLinkFi,
    CadDetailsDialogFi,
  },
  mixins: [
    accessPrivilegesMixin,
    breakpointMixin,
    shoppingcartMixin,
    catalogTextListMixin,
    onlineRetailersMixin,
    catalogAttributesMixin,
  ],
  inject: ['fields'],
  props: {
    catalogData: {
      type: Object,
      default: null,
    },
    mediaDownloadLink: {
      type: String,
      default: null,
    },
    noMarginBottom: Boolean,
  },
  emits: ['added-to-memolist'],
  data() {
    return {
      addToCartLoading: false,
      getRetailerAvailabilityForIds: [],
      stickyEnabled: false,
      hasLocalRetailers: false,
    };
  },
  apollo: {
    hasLocalRetailers: {
      query: HAS_LOCAL_RETAILERS,
      variables() {
        return {
          brand: this.$fischer.brandId(),
          productId: this.catalogData.ProductId,
          locale: this.$fischer.catalogLocale(),
        };
      },
      skip() {
        return IS_SSR || !this.localRetailers || !this.catalogData?.ProductId;
      },
    },
    getRetailerAvailabilityForIds: {
      query: GET_RETAILER_AVAILABILITY_FOR_IDS,
      variables() {
        return {
          baseSiteId: this.$fischer.baseSiteId(),
          productIds: this.productIds,
          useGtins: this.$fischer.useGtins(),
        };
      },
      skip() {
        return (
          IS_SSR || !this.isRetailConnectCart || this.productInCart || !this.catalogData.ProductId
        );
      },
    },
  },
  computed: {
    ...mapGetters({
      productDetailHeadMounted: 'layout/getProductDetailHeadMounted',
    }),
    productIds() {
      if (this.catalogData?.ProductId) {
        return [this.catalogData.ProductId];
      }

      return null;
    },
    localRetailers() {
      return this.$fischer.localRetailers();
    },
    retailerNotAvailable() {
      const stock =
        this.getRetailerAvailabilityForIds?.[0] && this.getRetailerAvailabilityForIds[0].stock;
      return stock === 'empty' || stock === null;
    },
    isShop() {
      return this.$fischer.shop.isClosedShop() || this.$fischer.shop.isOnlineShop();
    },
    isRetailConnect() {
      return this.$fischer.shop.isRetailConnect();
    },
    isRetailConnectCart() {
      return this.$fischer.shop.isRetailConnectCart();
    },
    productInCart() {
      let inCart = false;
      if (this.cart?.products) {
        inCart = this.cart.products.some(
          (product) => product.articleNo === this.catalogData.ProductId,
        );
      }
      return inCart;
    },
    apiFilters() {
      return this.$store.getters['product/apiFilters'];
    },
    articleNumber() {
      const artNr = this.catalogData?.ProductId && Number(this.catalogData.ProductId);
      return `${this.$dict.get('product-List.Table.Article-Number')} ${artNr}`;
    },
    categorySlug() {
      return (
        this.catalogData?.categoryData?.CategoryRole === 'FAMILY_PAGE' &&
        this.catalogData?.categoryData?.Slug &&
        `${this.$fischer.pages.products().url}${this.catalogData.categoryData.Slug}#productList`
      );
    },
    productName() {
      return this.textListValue(this.catalogData?.TextList, 'ECOMLANG');
    },
    features() {
      return this.textListValue(this.catalogData?.TextList, 'POCTOPFEATURESGEN');
    },
    eanCode() {
      const eanCode = this.attributeValue(this.catalogData?.Attributes, 'EAN_CODE');
      return eanCode ? `${this.$dict.get('product-List.Table.EAN-Code')}: ${eanCode}` : null;
    },
    hasCADLink() {
      return this.catalogData?.hasCADLink;
    },
    cadUrl() {
      if (
        !this.catalogData ||
        !this.catalogData.ProductId ||
        !this.fields.cadLink ||
        !this.fields.cadLink.value ||
        !this.fields.cadLanguage ||
        !this.fields.cadLanguage.value ||
        !this.fields.cadMandant ||
        !this.fields.cadMandant.value ||
        !this.fields.cadLinkText ||
        !this.fields.cadLinkText.value
      ) {
        return null;
      }
      return `${this.fields.cadLink.value}?mandant=${this.fields.cadMandant.value}&cul=${
        this.fields.cadLanguage.value
      }&CadBox=2&ArtikelNr=${Number(this.catalogData.ProductId)}`;
    },
    mediaDownloadFileName() {
      return this.catalogData?.categoryData?.categoryName
        ? `${this.catalogData.categoryData.categoryName}-${this.catalogData.ProductName}`
        : this.catalogData?.ProductName;
    },
  },
  mounted() {
    this.$store.dispatch('layout/setProductDetailHeadMounted', true);
  },
  created() {
    if (!IS_SSR && (this.isRetailConnectCart || this.isShop)) {
      this.getCurrentCart();
    }
  },
  methods: {
    getCurrentCart() {
      if (this.$fischer.cartId()) {
        try {
          this.getCart(false, true);
        } catch (error) {
          console.error('could not get current cart!', error);
        }
      }
    },
    openMediaDownloadDialog() {
      this.$refs.mediaDownloadDialog.show();
    },
    openRetailSearchDialog() {
      // TRACKING EVENT
      if (!IS_SSR) {
        const productName = `${this.catalogData.ProductId} - ${this.catalogData.ProductName}`;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'RCBuyLocal',
          productName,
        });
      }

      this.$refs.retailSearchDialog.show();
    },
    openProductRetailerDialog() {
      // TRACKING EVENT
      if (!IS_SSR) {
        const productName = `${this.catalogData.ProductId} - ${this.catalogData.ProductName}`;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'RCBuyProduct',
          productName,
        });
      }

      this.$refs.productRetailerDialog.show();
    },
    addProduct() {
      this.addToCartLoading = true;
      this.addProductToCart(
        { productID: this.catalogData.ProductId, productInitialQuantity: 1 },
        this.catalogData,
      ).then((res) => {
        if (res?.success) {
          this.addToCartLoading = false;
        }
      });
    },
    onOpenCadDetailsDialog() {
      this.$refs.cadDetailsDialog.open();
    },
    onIntersect(observer) {
      this.stickyEnabled = !observer[0].isIntersecting && observer[0].boundingClientRect.top > 0;
    },
  },
};
</script>

<style scoped src="./product-detail-head-fi_product-detail-head-fite-fi" />

<style src="./product-detail-head-fi_product-detail-head-fite-fi-unscoped" />
